/**
 * scroll-sequence-video.js
 * Upload to: assets/scroll-sequence-video.js
 *
 * Anti-stutter strategy:
 *  1. NO continuous RAF loop — we only seek in response to actual scroll events
 *  2. Seek-gating via isSeeking flag — we never queue a second seek until the
 *     browser fires the 'seeked' event for the current one. This is the #1
 *     cause of video stutter when driving currentTime from scroll.
 *  3. Pending-seek pattern — if a scroll happens while seeking, we store the
 *     latest target and apply it the moment 'seeked' fires. No seek is lost,
 *     no seeks pile up.
 *  4. Minimum seek threshold (16ms at 60fps) — tiny scroll deltas that would
 *     move the playhead less than one frame are ignored entirely.
 *  5. requestAnimationFrame used only to batch the actual seek call with the
 *     browser's paint cycle, not as a continuous polling loop.
 */

(function () {
  'use strict';

  /* ── Config ──────────────────────────────────────────────────────────── */
  var cfg = window.svsConfig;
  if (!cfg) return;

  var BREAKPOINT    = parseInt(cfg.mobileBreakpoint || 1024, 10);
  var SHOW_PROGRESS = cfg.showProgress !== false;

  /* ── Reduced-motion ──────────────────────────────────────────────────── */
  var motionQuery          = window.matchMedia('(prefers-reduced-motion: reduce)');
  var prefersReducedMotion = motionQuery.matches;

  /* ── DOM ─────────────────────────────────────────────────────────────── */
  var section     = document.querySelector('.svs-section');
  var outer       = section && section.querySelector('.svs-outer');
  var video       = section && section.querySelector('.svs-desktop .svs-video');
  var progressEl  = section && section.querySelector('.svs-progress');
  var progressBar = section && section.querySelector('.svs-progress-bar');

  if (!section || !outer) return;

  /* ── State ───────────────────────────────────────────────────────────── */
  var isDesktop   = false;
  var isVisible   = false;
  var duration    = 0;
  var isSeeking   = false;   // true while browser is processing a seek
  var pendingTime = -1;      // latest target while a seek was in progress (-1 = none)
  var lastTime    = -1;      // last time we actually seeked to
  var rafPending  = false;   // RAF scheduled flag

  /* Minimum time delta to bother seeking — 1 frame at 30fps ≈ 0.033s */
  var MIN_SEEK_DELTA = 0.033;

  /* ── Utilities ───────────────────────────────────────────────────────── */
  function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }

  /* ── Device mode ─────────────────────────────────────────────────────── */
  function isDesktopMode() {
    if (prefersReducedMotion) return false;
    if (window.screen && window.screen.fold && window.screen.fold.angle !== undefined) {
      if (window.screen.fold.angle < 120) return false;
    }
    return window.innerWidth > BREAKPOINT;
  }

  function applyDeviceMode() {
    var desktop = isDesktopMode();
    if (desktop === isDesktop) return;
    isDesktop = desktop;

    if (desktop) {
      document.documentElement.classList.add('svs-is-desktop');
      initDesktopVideo();
    } else {
      document.documentElement.classList.remove('svs-is-desktop');
      isSeeking   = false;
      pendingTime = -1;
      rafPending  = false;
    }
  }

  /* ── Video init ──────────────────────────────────────────────────────── */
  function initDesktopVideo() {
    if (!video) return;
    video.pause();

    if (video.readyState >= 1 && video.duration) {
      onVideoReady();
    } else {
      video.addEventListener('loadedmetadata', onVideoReady, { once: true });
      if (video.preload === 'none') video.preload = 'auto';
      video.load();
    }

    /* Seek-gate: when browser finishes a seek, apply any pending target */
    video.addEventListener('seeked', onSeeked);
  }

  function onVideoReady() {
    duration = video.duration;
    if (!duration || isNaN(duration)) return;

    /* Paint the correct frame for the current scroll position straight away */
    var t = getScrollProgress() * duration;
    doSeek(t);
  }

  /* ── Seek-gated seek ─────────────────────────────────────────────────── */
  /*
   * doSeek() is the ONLY place we set video.currentTime.
   * If a seek is already in progress, we store the request as pendingTime.
   * When 'seeked' fires, we apply pendingTime if one exists.
   * This eliminates the seek queue that causes stuttering.
   */
  function doSeek(time) {
    time = clamp(time, 0, duration);

    if (isSeeking) {
      /* Don't interrupt — store latest target and apply after current seek */
      pendingTime = time;
      return;
    }

    /* Skip if movement is sub-frame */
    if (lastTime >= 0 && Math.abs(time - lastTime) < MIN_SEEK_DELTA) return;

    isSeeking = true;
    lastTime  = time;

    try {
      video.currentTime = time;
    } catch (_) {
      isSeeking = false;
    }
  }

  function onSeeked() {
    isSeeking = false;

    if (pendingTime >= 0) {
      var t   = pendingTime;
      pendingTime = -1;
      doSeek(t);
    }
  }

  /* ── Scroll → progress ───────────────────────────────────────────────── */
  function getScrollProgress() {
    var rect     = outer.getBoundingClientRect();
    var viewH    = window.innerHeight || document.documentElement.clientHeight;
    var scrolled = -rect.top;
    var total    = rect.height - viewH;
    return clamp(scrolled / total, 0, 1);
  }

  /* ── Scroll handler ──────────────────────────────────────────────────── */
  /*
   * We use a single RAF per scroll burst to batch the seek with the next
   * paint. This avoids calling doSeek() multiple times per frame if the
   * browser fires several scroll events in one tick (common on trackpads).
   */
  function onScroll() {
    if (!isDesktop || !isVisible || !duration) return;
    if (rafPending) return; /* already scheduled */

    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      var progress = getScrollProgress();
      var t        = progress * duration;

      doSeek(t);

      if (SHOW_PROGRESS && progressBar) {
        progressBar.style.width = (progress * 100).toFixed(1) + '%';
        if (progressEl) progressEl.setAttribute('aria-valuenow', Math.round(progress * 100));
      }
    });
  }

  /* ── IntersectionObserver ────────────────────────────────────────────── */
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      isVisible = entries[0].isIntersecting;
    }, { rootMargin: '200px 0px' });
    io.observe(section);
  } else {
    isVisible = true;
  }

  /* ── ResizeObserver / resize ─────────────────────────────────────────── */
  var resizeTimer;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(applyDeviceMode, 150);
  }

  if ('ResizeObserver' in window) {
    new ResizeObserver(onResize).observe(section);
  } else {
    window.addEventListener('resize', onResize, { passive: true });
  }

  /* ── Screen Fold API ─────────────────────────────────────────────────── */
  if (window.screen && window.screen.fold) {
    window.screen.fold.addEventListener('change', onResize);
  }

  /* ── Orientation change ──────────────────────────────────────────────── */
  window.addEventListener('orientationchange', function () {
    setTimeout(onResize, 300);
  }, { passive: true });

  /* ── Reduced-motion runtime change ──────────────────────────────────── */
  motionQuery.addEventListener('change', function (e) {
    prefersReducedMotion = e.matches;
    onResize();
  });

  /* ── Scroll listener ─────────────────────────────────────────────────── */
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ── bfcache ─────────────────────────────────────────────────────────── */
  window.addEventListener('pagehide', function () {
    rafPending  = false;
    isSeeking   = false;
    pendingTime = -1;
  });

  /* ── Mobile autoplay ─────────────────────────────────────────────────── */
  var mobileVideo = section.querySelector('.svs-mobile .svs-video--mobile');
  if (mobileVideo) {
    var attempt = mobileVideo.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () { mobileVideo.load(); });
    }
  }

  /* ── Init ────────────────────────────────────────────────────────────── */
  applyDeviceMode();

})();